import type { DeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';

export type AutoTrackingSurface = 'bottom_tray' | 'video_overlay' | 'control_toolbar' | 'unknown';

export type AutoTrackingSubjectSource = 'manual_selection' | 'redetection' | 'automatic';

export type AutoTrackingReason =
  | 'user_disabled'
  | 'manual_override'
  | 'subject_missing'
  | 'worker_error'
  | 'reservation_lost'
  | 'unknown';

export type AutoTrackingAnalyticsSnapshot = Readonly<{
  hasAssignment: boolean;
  hasLostSubject: boolean;
  hasReservation: boolean;
  isAnnotationSelected: boolean;
  isRedetecting: boolean;
  isTracking: boolean;
}>;

export type AutoTrackingAnalyticsPendingState = Readonly<{
  nextStartSurface?: AutoTrackingSurface;
  nextStopReason?: AutoTrackingReason;
  nextSubjectSource?: AutoTrackingSubjectSource;
}>;

type AutoTrackingLifecycleEvent = Readonly<{
  name:
    | typeof EVENT_NAMES.AUTO_TRACKING_ACTIVATED
    | typeof EVENT_NAMES.AUTO_TRACKING_STARTED
    | typeof EVENT_NAMES.AUTO_TRACKING_STOPPED
    | typeof EVENT_NAMES.AUTO_TRACKING_SUBJECT_ACQUIRED
    | typeof EVENT_NAMES.AUTO_TRACKING_SUBJECT_LOST;
  properties: EventProperties;
}>;

const SUBJECT_LOST_REASON: AutoTrackingReason = 'subject_missing';

const clearPendingField = <Key extends keyof AutoTrackingAnalyticsPendingState>(
  pending: AutoTrackingAnalyticsPendingState,
  key: Key
): AutoTrackingAnalyticsPendingState => {
  const nextPending = { ...pending } as Partial<AutoTrackingAnalyticsPendingState>;

  delete nextPending[key];

  return nextPending;
};

const resolveStopReason = (
  previous: AutoTrackingAnalyticsSnapshot,
  next: AutoTrackingAnalyticsSnapshot,
  pending: AutoTrackingAnalyticsPendingState
): AutoTrackingReason => {
  if (pending.nextStopReason) {
    return pending.nextStopReason;
  }

  if (next.hasLostSubject || previous.isRedetecting) {
    return SUBJECT_LOST_REASON;
  }

  if (previous.hasReservation && !next.hasReservation) {
    return 'reservation_lost';
  }

  if (previous.hasAssignment && !next.hasAssignment) {
    return 'worker_error';
  }

  return 'unknown';
};

export const deriveAutoTrackingLifecycleEvents = (
  previous: AutoTrackingAnalyticsSnapshot,
  next: AutoTrackingAnalyticsSnapshot,
  deviceAnalyticsContext: DeviceAnalyticsContext,
  pending: AutoTrackingAnalyticsPendingState = {}
): {
  events: AutoTrackingLifecycleEvent[];
  pending: AutoTrackingAnalyticsPendingState;
} => {
  const events: AutoTrackingLifecycleEvent[] = [];
  let nextPending = { ...pending };

  // Fires once when the auto-tracking feature is first enabled on this device
  // (reservation created: hasReservation false→true). Distinct from STARTED,
  // which fires per tracking session. Per ENG-3960 §A4.
  if (!previous.hasReservation && next.hasReservation) {
    events.push({
      name: EVENT_NAMES.AUTO_TRACKING_ACTIVATED,
      properties: deviceAnalyticsContext
    });
  }

  const emitSubjectAcquired = (subjectSource: AutoTrackingSubjectSource): void => {
    events.push({
      name: EVENT_NAMES.AUTO_TRACKING_SUBJECT_ACQUIRED,
      properties: {
        ...deviceAnalyticsContext,
        subject_source: subjectSource
      }
    });
  };

  if (!previous.isTracking && next.isTracking) {
    events.push({
      name: EVENT_NAMES.AUTO_TRACKING_STARTED,
      properties: {
        ...deviceAnalyticsContext,
        surface: nextPending.nextStartSurface ?? 'unknown'
      }
    });

    nextPending = clearPendingField(nextPending, 'nextStartSurface');

    if (next.isAnnotationSelected) {
      emitSubjectAcquired(nextPending.nextSubjectSource ?? 'automatic');
      nextPending = clearPendingField(nextPending, 'nextSubjectSource');
    }
  } else if (!previous.isAnnotationSelected && next.isAnnotationSelected && next.isTracking) {
    emitSubjectAcquired(
      nextPending.nextSubjectSource ?? (previous.isRedetecting ? 'redetection' : 'automatic')
    );
    nextPending = clearPendingField(nextPending, 'nextSubjectSource');
  }

  if (previous.isTracking && !previous.isRedetecting && next.isRedetecting) {
    events.push({
      name: EVENT_NAMES.AUTO_TRACKING_SUBJECT_LOST,
      properties: {
        ...deviceAnalyticsContext,
        reason: SUBJECT_LOST_REASON
      }
    });
  }

  if (previous.isTracking && !next.isTracking) {
    events.push({
      name: EVENT_NAMES.AUTO_TRACKING_STOPPED,
      properties: {
        ...deviceAnalyticsContext,
        reason: resolveStopReason(previous, next, nextPending)
      }
    });

    nextPending = clearPendingField(nextPending, 'nextStopReason');
  }

  return {
    events,
    pending: nextPending
  };
};
